import {l} from "types/typescriptdefinisjoner_lederplan";
import {BasisRessurs} from "../../infrastruktur/BasisRessurs";
import {RessursFeilhåndtering} from "../RessursFeilhåndtering";
import _ from "lodash";
import {regNavn} from "../tredjepartsTjenestenavn";
import {gettextCatalog} from "angular-gettext";
import {sisteElementEtter} from "../Strenger";
import {Språk} from "../i18n/Spraak";
import {Debugger} from "debug";
import {resource} from "angular";

const debug: Debugger = require('debug')('BrukerRessurs');

export class BrukerRessurs extends BasisRessurs<l.BrukerModell> {
    static regNavn = "brukere";

    static forInjisering() {
        return [
            "$resource",
            RessursFeilhåndtering.regNavn,
            regNavn.gettext.gettextCatalog,
            BrukerRessurs
        ]
    }

    constructor($resource: ng.resource.IResourceService,
                ressursFeilhåndtering: RessursFeilhåndtering,
                gettextCatalog: gettextCatalog) {
        super("bruker",
            $resource,
            ressursFeilhåndtering,
            gettextCatalog,
            {
                finnAlle: {method: 'GET', url: '/api/bruker/finnAlle', isArray: true},
                aktivitetHistogramAlleBrukere: {method: 'GET', url: '/api/bruker/aktivitetHistogramAlleBrukere', isArray: false},
                aktivitetHistogram: {method: 'GET', url: '/api/bruker/:brukerId/aktivitetHistogram', isArray: false},
                revisjonsloggAggregater: {method: 'GET', url: '/api/bruker/:brukerId/revisjonsloggAggregater', isArray: true},
                aktiveAutentiseringstoken: {method: 'GET', url: '/api/bruker/:brukerId/aktiveAutentiseringstoken', isArray: true},
                kallendeBrukersGrupper: {method: 'GET', url: '/api/bruker/kallendeBrukersGrupper', isArray: true},
                brukersGrupper: {method: 'GET', url: '/api/bruker/:brukerId/grupper', isArray: true},
                hentTilgjengeligeTidssoner: { method: 'GET', url: '/api/bruker/hentTilgjengeligeTidssoner', isArray: true},
                finnBrukerMedBrukernavn: { method: 'GET', url: '/api/bruker/finnBrukerMedBrukernavn/:brukernavn'},
                kobleBrukere: { method: 'POST', url: '/api/bruker/kobleBrukere'},
                registrerPushToken: {method: "PUT", url: '/api/bruker/:id/registrerPushToken'},
                persisterBrukervalg: {method: "POST", url: '/api/bruker/persisterBrukervalg'},
                oppdaterSpraak: {method: "POST", url: '/api/bruker/oppdaterSpraak'},
                oppdatertidssone: {method: "POST", url: '/api/bruker/oppdatertidssone'},
                validerPassord: {method: 'POST', url: '/api/bruker/validerPassord'},
                oppdaterV2: {method: "POST", url: "/api/bruker/:id/oppdater"},
                oppdaterSensitiveData: {method: "POST", url: "/api/bruker/:id/oppdaterSensitiveData"},
                oppdaterMeldingsinnstillinger: {method: "POST", url: '/api/bruker/:id/oppdaterMeldingsinnstillinger'},
                ønskerNyttPassord: {method: "POST", url: "/api/bruker/onskerNyttPassord"},
                nyttPassordAngitt: {method: "POST", url: "/api/bruker/nyttPassordAngitt"},
                nyttPassord: {method: "POST", url: "/api/bruker/nyttPassord"},
                validerEpostOppdateresTil: {method: 'POST', url: '/api/bruker/:brukerId/validerEpostOppdateresTil', params: {brukerId: '@brukerId'}},
                validerBrukernavnOppdateresTil: {method: 'POST', url: '/api/bruker/:brukerId/validerBrukernavnOppdateresTil', params: {brukerId: '@brukerId'}},
                validerBrukernavn: {method: 'POST', url: '/api/bruker/validerBrukernavn'},
                bekreftEpostadresse: {method: 'POST', url: '/api/bruker/bekreftEpostadresse'},
                sendBekreftEpostadresseToken: {method: 'POST', url: '/api/bruker/sendBekreftEpostadresseToken'},
                bekreftMobilnummerOtp: {method: 'POST', url: '/api/bruker/bekreftMobilnummerOtp'},
                sendBekreftMobilnummerOtp: {method: 'POST', url: '/api/bruker/sendBekreftMobilnummerOtp'}
            });
    }

    finnAlle(): l.EnkelBrukerModell[] {
        return this.ressurs["finnAlle"](
            {},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste alle brukere")));
    }

    aktivitetHistogramAlleBrukere(): l.AngularRessurs<any> {
        return this.ressurs["aktivitetHistogramAlleBrukere"](
            {},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste brukeraktivitetsdata")));
    }

    aktivitetHistogram(brukerId: string): l.AngularRessurs<any> {
        return this.ressurs["aktivitetHistogram"](
            {brukerId:brukerId},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste brukeraktivitetsdata")));
    }

    revisjonsloggAggregater(brukerId: string) {
        return this.ressurs["revisjonsloggAggregater"](
            {brukerId: brukerId},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste revisjonsloggdata"))).$promise
            .then(aggregater => {
                let stats = {
                    moeter: {opprettet: 0, endret: 0, slettet: 0},
                    saker: {opprettet: 0, endret: 0, slettet: 0},
                    dokumenter: {opprettet: 0, endret: 0, slettet: 0},
                    oppgaver: {opprettet: 0, endret: 0, slettet: 0},
                    beslutninger: {opprettet: 0, endret: 0, slettet: 0}
                };
                aggregater.forEach(aggregat => this.populerEntitetTypeStats(stats, aggregat));
                return stats;
            });
    }

    aktiveAutentiseringstoken(brukerId: string): l.TokeninformasjonModell[] {
        debug("aktiveAutentiseringstoken", brukerId);
        return this.ressurs["aktiveAutentiseringstoken"](
            {brukerId: brukerId},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste aktive token")));
    }

    /**
     * følgende er ikke inkludert (men finnes i aggregat):
     * Gruppe
     * Medlem
     * Tema
     * Møtedeltaker
     * Sakstype
     * Bruker
     * MedlemsRolle
     */
    private populerEntitetTypeStats(stats, aggregat: l.HendelseAggregat) {
        switch (sisteElementEtter(aggregat.entitetType, ".")) {
            case "Møte":
                return this.populerEndringstypeStats(stats.moeter, aggregat);
            case "Sak":
                return this.populerEndringstypeStats(stats.saker, aggregat);
            case "Dokument":
                return this.populerEndringstypeStats(stats.dokumenter, aggregat);
            case "Oppgave":
                return this.populerEndringstypeStats(stats.oppgaver, aggregat);
            case "Beslutning":
                return this.populerEndringstypeStats(stats.beslutninger, aggregat);
        }
    }

    private populerEndringstypeStats(stats, aggregat: l.HendelseAggregat) {
        switch (aggregat.endringstype) {
            case l.Endringstype.opprett:
                return stats.opprettet = aggregat.antall;
            case l.Endringstype.rediger:
                return stats.endret = aggregat.antall;
            case l.Endringstype.slett:
                return stats.slettet = aggregat.antall;
        }
    }

    kallendeBrukersGrupper(): l.MedlemsRolleIGruppeModell[] {
        return this.ressurs['kallendeBrukersGrupper'](
            {},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finner brukers grupper")));
    }

    brukersGrupper(brukerId: string): l.MedlemsRolleIGruppeModell[] {
        return this.ressurs['brukersGrupper'](
            {brukerId: brukerId},
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString("Kunne ikke laste gruppene til bruker {{id}}", {id:brukerId})));
    }

    hentTilgjengeligeTidssoner():l.AngularRessurs<string[]> {
        debug("hentTilgjengeligeTidssoner");
        return this.ressurs["hentTilgjengeligeTidssoner"](
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString("Kunne ikke hente tilgjengelige tidssoner")));
    }

    registrerPushToken(brukerId: string, deviceId:string, type:l.AppPushTokenType, token: string): l.OkRespons {
        return this.ressurs["registrerPushToken"](
            {
                id: brukerId,
                deviceId: deviceId,
                type: type,
                token: token
            },
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke registrerer brukers pushmeldingstoken")));
    }

    persisterBrukervalg(brukervalg: any): l.AngularRessurs<any> {
        return this.ressurs["persisterBrukervalg"](
            {brukervalg: (brukervalg)},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke persistere brukervalg")));
    }

    oppdaterMeldingsinnstillinger(brukerId:string, spesifikasjon:l.MeldingsinnstillingerSpesifikasjon): l.BrukerModell {
        return this.ressurs["oppdaterMeldingsinnstillinger"](
            _.assign({id:brukerId}, spesifikasjon),
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke persistere meldingsvalg")));
    }

    oppdaterSpråk(språk: Språk): l.AngularRessurs<any> {
        return this.ressurs["oppdaterSpraak"](
            {spraak: (språk.servertag)},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke oppdatere brukerprofil med valgt språk")));
    }

    oppdaterTidssone(tidssone:string):l.AngularRessurs<any> {
        debug("oppdatertidssone", tidssone);
        return this.ressurs["oppdatertidssone"](
            {tidssone: tidssone},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke oppdatere brukerprofil med valgt tidssone")));
    }

    ønskerNyttPassord(brukernavnEllerEpost: string): l.OkRespons {
        return this.ressurs["ønskerNyttPassord"](
            {brukernavnEllerEpost: brukernavnEllerEpost},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Feil under resetting av passord")));
    }

    nyttPassordAngitt(nyttPassord: l.NyttPassordFraTokenModell): l.OkRespons {
        return this.ressurs["nyttPassordAngitt"](
            nyttPassord,
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Feil under lagring av passord")));
    }

    nyttPassord(nyttPassord: l.NyttPassordModell, vedOk: Function = _.noop, vedFeil: Function = _.noop) {
        return this.ressurs["nyttPassord"](nyttPassord, vedOk, vedFeil);
    }

    validerPassord(passord: string): l.PassordValideringsresultat {
        return this.ressurs["validerPassord"](
            {passord: passord},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Feil under validering av passord")));
    }

    kobleBrukere(spekk: l.SlåSammenBrukereSpesifikasjon): l.ValideringsresultatModell<l.SlåSammenBrukereValideringsfeil> {
        return this.ressurs["kobleBrukere"](
          spekk,
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Feil ved kobling av brukere")));
    }

    finnBrukerMedBrukernavn(brukernavn:string, eksternHåndtering: (feil: resource.IResourceResponse) => boolean):l.BrukerModell{
        return this.ressurs["finnBrukerMedBrukernavn"](
          {brukernavn: brukernavn},
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Feil ved søk etter bruker med brukernavn"),eksternHåndtering));
    }

    validerBrukernavn(brukernavn: string): l.BrukernavnValideringsresultat {
        return this.ressurs["validerBrukernavn"](
            {brukernavn: brukernavn},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Feil under validering av brukernavn")));
    }

    bekreftEpostadresse(token: string): l.ValideringResponsModell {
        return this.ressurs["bekreftEpostadresse"](
            {token: token},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Feil under verifisering av e-postadresse")));
    }

    sendBekreftEpostadresseToken(): l.BekreftEpostadresseSendtModell {
        return this.ressurs["sendBekreftEpostadresseToken"](
            {},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Feil ved sending av e-post for å verifisere e-postadresse")));
    }

    bekreftMobilnummerOtp(otp: string): l.ValideringResponsModell {
        return this.ressurs["bekreftMobilnummerOtp"](
            {otp: otp},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Feil under verifisering av mobilnummer")));
    }

    sendBekreftMobilnummerOtp(): l.BekreftMobilnummerOtpSendtModell {
        return this.ressurs["sendBekreftMobilnummerOtp"](
            {},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Feil under sending av engangskode for verifisering av mobilnummer")));
    }

    oppdaterSensitiveData(spesifikasjon: l.OppdaterBrukerSensitivSpesifikasjon): l.OppdatertSensitiveDataModell {
        return this.ressurs["oppdaterSensitiveData"](
          spesifikasjon,
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Feil ved oppdatering av e-post og mobilnummer")));
    }

    // kaller POST /bruker/:id/oppdater, men den i superklassen kaller POST /bruker/:id/ sistnevte brukes av gamle versjoner av mobil-app.. Se BrukerRessurs.java/oppdaterV2
    override oppdater(spesifikasjon: l.OppdaterBrukerSpesifikasjon): l.BrukerModell {
        return this.ressurs["oppdaterV2"](
          spesifikasjon,
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Feil ved oppdatering av navn og avatar")));
    }


    validerEpostOppdateresTil(epostSpesifikasjon: l.EpostModell): l.EpostValideringsresultat {
        return this.ressurs["validerEpostOppdateresTil"](
          {brukerId:epostSpesifikasjon.brukerId, epost:epostSpesifikasjon.epost},
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Feil under validering av e-post")));
    }

    validerBrukernavnOppdateresTil(brukerId:string, brukernavn: string): l.BrukernavnValideringsresultat {
        return this.ressurs["validerBrukernavnOppdateresTil"](
          {brukerId:brukerId, brukernavn:brukernavn},
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Feil under validering av brukernavn")));
    }
}
